import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, Bell, User, Inbox, PlusCircle, Users, Settings, LogOut, MapPin } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Btn, Input } from "./kit";

const NAV = {
  admin: [
    ["/a/jobs", Briefcase, "Jobs"], ["/a/jobs/new", PlusCircle, "New"], ["/a/specialists", Users, "Team"],
    ["/a/alerts", Bell, "Alerts"], ["/a/settings", Settings, "Settings"],
  ],
  specialist: [["/s/offers", Inbox, "Offers"], ["/s/jobs", Briefcase, "My jobs"], ["/s/alerts", Bell, "Alerts"], ["/s/profile", User, "Profile"]],
};

function useAlerts() {
  const [unread, setUnread] = useState(0);
  const seen = useRef(null);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!alive) return;
        setUnread(data.filter((n) => !n.read).length);
        if (seen.current) data.filter((n) => !seen.current.has(n.id) && !n.read).forEach((n) => toast(n.title, { description: n.body }));
        seen.current = new Set(data.map((n) => n.id));
      } catch {
        /* offline */
      }
    };
    poll();
    const t = setInterval(poll, 12000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  return unread;
}

export function AvailabilityToggle() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  const [zip, setZip] = useState("");
  const [addr, setAddr] = useState("");
  const send = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/availability", body);
      setUser(data);
      setManual(false);
      toast.success(data.available ? "You're Available" : "You're Offline");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const goAvailable = () => {
    if (!navigator.geolocation) return setManual(true);
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => send({ available: true, lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {
        setBusy(false);
        setManual(true);
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };
  const on = user?.available;
  return (
    <>
      <button
        data-testid="availability-toggle"
        disabled={busy}
        onClick={() => (on ? send({ available: false }) : goAvailable())}
        className={`flex items-center gap-2 h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${
          on ? "border-emerald-700 text-emerald-400 bg-emerald-950/30" : "border-[#333] text-neutral-400"
        }`}
      >
        <span className={`w-2 h-2 rounded-full ${on ? "bg-emerald-400" : "bg-neutral-600"}`} />
        {busy ? "…" : on ? "Available" : "Offline"}
      </button>
      {manual && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="manual-location-modal">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3 fade-in">
            <div className="font-head text-xl uppercase flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF6B00]" />Your location</div>
            <p className="text-sm text-neutral-400">Location permission unavailable. Enter where you are starting from. Distances are approximate.</p>
            <Input data-testid="manual-address-input" placeholder="Street address (optional)" value={addr} onChange={(e) => setAddr(e.target.value)} />
            <Input data-testid="manual-zip-input" placeholder="ZIP code" inputMode="numeric" value={zip} onChange={(e) => setZip(e.target.value)} />
            <div className="flex gap-2">
              <Btn variant="outline" className="flex-1" onClick={() => setManual(false)}>Cancel</Btn>
              <Btn data-testid="manual-location-save" className="flex-1" loading={busy} onClick={() => send({ available: true, address: addr, zip })}>Go Available</Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const unread = useAlerts();
  const items = NAV[user.role];
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
      <header className="no-print sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#1f1f1f]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="font-head text-xl font-bold uppercase tracking-wide" data-testid="app-brand">
            Driven<span className="text-[#FF6B00]">.</span> Auto Care
          </div>
          <div className="flex items-center gap-2">
            {user.role === "specialist" && <AvailabilityToggle />}
            <button data-testid="logout-btn" onClick={() => { const r = user.role; logout(); nav(r === "admin" ? "/admin/login" : "/login"); }} className="p-2 text-neutral-500 hover:text-white" aria-label="Log out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 pt-4 pb-28">
        <Outlet />
      </main>
      <nav className="no-print fixed bottom-0 inset-x-0 z-50 bg-[#101010]/95 backdrop-blur-md border-t border-[#1f1f1f]">
        <div className="max-w-3xl mx-auto flex justify-around px-2 pb-[env(safe-area-inset-bottom)]">
          {items.map(([to, Icon, label]) => (
            <NavLink
              key={to}
              to={to}
              end
              data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `relative flex flex-col items-center gap-1 py-2.5 px-3 text-[11px] font-medium ${isActive ? "text-[#FF6B00]" : "text-neutral-500"}`}
            >
              <Icon className="w-5 h-5" />
              {label}
              {label === "Alerts" && unread > 0 && (
                <span className="absolute top-1.5 right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#FF6B00] text-[10px] text-white flex items-center justify-center" data-testid="alerts-unread-badge">{unread}</span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
